import { useCallback, useEffect, useRef, useState } from "react";
import { ChevronDown, Heart } from "lucide-react";

const CLIENT_ID = process.env.REACT_APP_PAYPAL_CLIENT_ID;
const BUTTON_ID = process.env.REACT_APP_PAYPAL_BUTTON_ID;
const SDK_URL = `https://www.paypal.com/sdk/js?client-id=${CLIENT_ID}&components=hosted-buttons&disable-funding=venmo&currency=SGD`;

let sdkPromise = null;
const loadSdk = () => {
  if (window.paypal?.HostedButtons) return Promise.resolve(window.paypal);
  if (!sdkPromise) {
    sdkPromise = new Promise((resolve, reject) => {
      const s = document.createElement("script");
      s.src = SDK_URL;
      s.async = true;
      s.onload = () => resolve(window.paypal);
      s.onerror = () => {
        sdkPromise = null;
        reject(new Error("PayPal SDK failed to load"));
      };
      document.body.appendChild(s);
    });
  }
  return sdkPromise;
};

const PayPalButton = ({ onFail }) => {
  const ref = useRef(null);

  useEffect(() => {
    let active = true;
    loadSdk()
      .then((paypal) => {
        if (!active || !ref.current) return;
        ref.current.replaceChildren();
        return paypal.HostedButtons({ hostedButtonId: BUTTON_ID }).render(ref.current);
      })
      .catch(() => active && onFail());
    return () => {
      active = false;
    };
  }, [onFail]);

  return (
    <div
      id={`paypal-container-${BUTTON_ID}`}
      ref={ref}
      data-testid="paypal-button-container"
      className="mt-3 max-w-[240px] [zoom:0.8] min-h-[40px]"
    />
  );
};

export const PayPalSupport = () => {
  const [expanded, setExpanded] = useState(false);
  const [failed, setFailed] = useState(false);
  const onFail = useCallback(() => setFailed(true), []);

  return (
    <div data-testid="paypal-support" className="px-5 py-3 border-t border-[#E5E2DA]">
      <button
        type="button"
        data-testid="paypal-support-toggle"
        aria-expanded={expanded}
        onClick={() => setExpanded((v) => !v)}
        className="inline-flex items-center gap-1.5 font-mono text-[10px] tracking-widest uppercase text-[#8C7A6B] hover:text-[#C17767] transition-colors"
      >
        <Heart size={10} strokeWidth={2} className="text-[#C17767]" />
        Enjoying the guide? Support it.
        <ChevronDown
          size={11}
          className={`transition-transform ${expanded ? "rotate-180" : ""}`}
        />
      </button>
      {expanded && (
        failed ? (
          <p data-testid="paypal-support-error" className="mt-2 text-xs text-[#8C7A6B]">
            PayPal couldn&apos;t load right now. Please try again later.
          </p>
        ) : (
          <div className="rise">
            <PayPalButton onFail={onFail} />
          </div>
        )
      )}
    </div>
  );
};
